/**
 * TrackStatsCalculator — computes summary statistics over a TrackPoint[]
 * using haversine for leg distance and straightforward min/max/avg for the
 * per-axis metrics. Used by the pure-TS importer path AND as a post-
 * processing step on DuckDB-produced tracks so both paths emit the same
 * numbers.
 */
import type { TrackPoint, TrackStats } from './track-parsers/track-types';

const DEG = Math.PI / 180;
const NM_PER_RAD = 3440.065;

function haversineNm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = (lat2 - lat1) * DEG;
  const dLng = (lng2 - lng1) * DEG;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLng / 2) ** 2;
  return 2 * Math.asin(Math.sqrt(a)) * NM_PER_RAD;
}

export function computeTrackStats(points: TrackPoint[]): TrackStats {
  if (points.length === 0) {
    return {
      distanceNm: 0, durationMs: 0, maxAltFt: 0, minAltFt: 0,
      maxGsKts: 0, avgGsKts: 0, startTime: null, endTime: null,
      climbFt: 0, descentFt: 0,
    };
  }

  let distance = 0;
  let climb = 0;
  let descent = 0;
  let minAlt = Infinity;
  let maxAlt = -Infinity;
  let maxGs = 0;
  let gsSum = 0;
  let gsCount = 0;
  let minTime: number | null = null;
  let maxTime: number | null = null;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];

    if (p.altitudeFt !== undefined && Number.isFinite(p.altitudeFt)) {
      if (p.altitudeFt < minAlt) minAlt = p.altitudeFt;
      if (p.altitudeFt > maxAlt) maxAlt = p.altitudeFt;
    }
    if (p.groundSpeedKts !== undefined && Number.isFinite(p.groundSpeedKts)) {
      if (p.groundSpeedKts > maxGs) maxGs = p.groundSpeedKts;
      gsSum += p.groundSpeedKts;
      gsCount++;
    }
    if (p.timestamp !== undefined && Number.isFinite(p.timestamp)) {
      if (minTime === null || p.timestamp < minTime) minTime = p.timestamp;
      if (maxTime === null || p.timestamp > maxTime) maxTime = p.timestamp;
    }

    if (i > 0) {
      const prev = points[i - 1];
      distance += haversineNm(prev.latitude, prev.longitude, p.latitude, p.longitude);
      if (prev.altitudeFt !== undefined && p.altitudeFt !== undefined) {
        const delta = p.altitudeFt - prev.altitudeFt;
        if (delta > 0) climb += delta;
        else descent += Math.abs(delta);
      }
    }
  }

  return {
    distanceNm: distance,
    durationMs: (minTime !== null && maxTime !== null) ? (maxTime - minTime) : 0,
    maxAltFt: Number.isFinite(maxAlt) ? maxAlt : 0,
    minAltFt: Number.isFinite(minAlt) ? minAlt : 0,
    maxGsKts: maxGs,
    avgGsKts: gsCount > 0 ? gsSum / gsCount : 0,
    startTime: minTime,
    endTime: maxTime,
    climbFt: climb,
    descentFt: descent,
  };
}
